import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useReducer, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import classNames from "classnames";
import { Calendar } from "primereact/calendar";
import { Fieldset } from "primereact/fieldset";
import { Button } from "primereact/button";

import { UpdateInvoiceSummaryDialogProps } from "./updateInvoiceSummaryConsts";
import { SummaryDetailsTable } from "../summaryDetailsTable.tsx";
import { InvoiceSummaryService } from "../../InvoiceSummaryService";
import {
  InvoiceSummaryFieldType,
  ODataPeriod,
  OInvoiceGoodsType,
  OInvoiceSummaryField,
  UpdateInvoiceSummaryFormState,
} from "../../../../shared/constants";
import { ToastService } from "../../../../AppToast";
import { RegisterInvoiceService } from "../../../../service/invoice/RegisterInvoiceService";
import moment from "moment";
import { getMQDropdownValues } from "../../invoiceSummaryUtils";

export const UpdateInvoiceSummaryDialog = ({
  onHide,
  open,
  isEditMode,
  invoiceSummaryData,
  currencies,
  refreshLazyData,
  disableSubmit = false,
}: UpdateInvoiceSummaryDialogProps) => {
  const [summaryDataDetails, setSummaryDataDetails] = useState([]);
  const [availableSerials, setAvailableSerials] = useState([]);
  const [selectedSerial, setSelectedSerial] = useState("");
  const [disableSubmitBtn, setDisableSumbitBtn] = useState(false);
  const [hideSubmitBtn, setHideSubmitBtn] = useState(false);
  const [formState, dispatch] = useReducer(
    (
      state: UpdateInvoiceSummaryFormState,
      action: { type: InvoiceSummaryFieldType | string; payload: any }
    ) => {
      switch (action.type) {
        case OInvoiceSummaryField.DATA_PERIOD:
          if (action.payload === ODataPeriod.DAY) {
            return { ...state, dataPeriod: action.payload, dataPeriodDate: "" };
          } else {
            return { ...state, dataPeriod: action.payload };
          }
        case OInvoiceSummaryField.DATA_PERIOD_DATE:
          return { ...state, dataPeriodDate: action.payload };
        case OInvoiceSummaryField.MONTH_OR_QUARTER:
          return { ...state, monthOrQuarter: action.payload };
        case OInvoiceSummaryField.YEAR:
          return { ...state, year: action.payload };
        case OInvoiceSummaryField.CREATION_DATE:
          return { ...state, creationDate: action.payload };
        case OInvoiceSummaryField.IS_FIRST_SUBMISSION:
          return { ...state, isFirstSubmission: action.payload };
        case OInvoiceSummaryField.CURRENCY_CODE:
          return { ...state, currencyCode: action.payload };
        case OInvoiceSummaryField.STATUS:
          return { ...state, status: action.payload };
        case OInvoiceSummaryField.TOTAL_SUBMIT:
          return { ...state, totalSubmit: action.payload };
        case OInvoiceSummaryField.SUMMARY_DATA_NUMBER:
          return { ...state, summaryDataNumber: action.payload };
        case OInvoiceSummaryField.GOODS_TYPE:
          return { ...state, goodsType: action.payload };
        case "multiTax":
          return { ...state, multiTax: action.payload };
        case "all":
          return { ...state, ...action.payload };
        default:
          return state;
      }
    },
    {
      dataPeriod: ODataPeriod.DAY,
      dataPeriodDate: "",
      monthOrQuarter: "",
      totalSubmit: 0,
      year: "",
      isFirstSubmission: false,
      currencyCode: "VND",
      type: "other",
      multiTax: false,
      creationDate: "",
    }
  );

  const handleSubmit = () => {
    const validateFormState = (
      state: UpdateInvoiceSummaryFormState
    ): string[] => {
      const errors: string[] = [];

      // Validate dataPeriod
      if (!state.dataPeriod) {
        errors.push("Kỳ dữ liệu không được để trống");
      }

      // Validate dataPeriodDate for daily period
      if (state.dataPeriod === ODataPeriod.DAY && !state.dataPeriodDate) {
        errors.push("Date is required for daily period");
      }

      // Validate monthOrQuarter for monthly/quarterly periods
      if (state.dataPeriod !== ODataPeriod.DAY) {
        if (!state.monthOrQuarter) {
          errors.push("Month/Quarter is required");
        } else {
          const value = parseInt(state.monthOrQuarter);
          if (
            state.dataPeriod === ODataPeriod.MONTH &&
            (value < 1 || value > 12)
          ) {
            errors.push("Tháng phải nằm trong khoảng từ 1 đến 12");
          }
          if (
            state.dataPeriod === ODataPeriod.QUARTER &&
            (value < 1 || value > 4)
          ) {
            errors.push("Quý phải nằm trong khoảng từ 1 đến 4");
          }
        }
      }

      // Validate year
      if (state.dataPeriod !== ODataPeriod.DAY && !state.year) {
        errors.push("Năm không được để trống");
      }
      if (state.dataPeriod !== ODataPeriod.DAY && !/^\d{4}$/.test(state.year)) {
        errors.push("Năm phải là một số nguyên có 4 chữ số");
      }

      // Validate currencyCode
      if (!state.currencyCode) {
        errors.push("Mã tiền tệ không được để trống");
      }

      if (!state.isFirstSubmission && !state.totalSubmit && !isEditMode) {
        errors.push("Số lần bổ sung không được để trống");
      }

      // Validate type
      if (!state.type) {
        errors.push("Loại hóa đơn không được để trống");
      }

      if (summaryDataDetails.length === 0) {
        errors.push("Chưa có hóa đơn được chọn");
      }

      return errors;
    };

    const validationErrors = validateFormState(formState);

    if (validationErrors.length > 0) {
      ToastService.error(`${validationErrors[0]}`);
      return;
    }

    const orderedSummaryDataDetails = summaryDataDetails.map(
      (detail: any, index: number): any => {
        return {
          ...(() => {
            const { vatName, ...rest } = detail;
            return rest;
          })(),
          orders: index + 1,
          vatRate: detail?.vatRate?.toString(),
        };
      }
    );

    const apiBody = {
      summaryDataDetails: orderedSummaryDataDetails,
      creationDate: moment(formState.creationDate).format("YYYY-MM-DD"),
      currencyCode: formState.currencyCode,
      dataPeriod: formState.dataPeriod,
      supplementTimes: parseInt(formState.totalSubmit),
      note: null,
      goodsType: formState.goodsType,
      isFirstSubmission: formState.isFirstSubmission,
      summaryDataNumber: formState.summaryDataNumber,
      year:
        formState.dataPeriod === ODataPeriod.DAY
          ? null
          : parseInt(formState.year),
      ...(formState.dataPeriod !== ODataPeriod.DAY && {
        monthOrQuarter: parseInt(formState.monthOrQuarter),
      }),
      ...(formState.dataPeriod === ODataPeriod.DAY && {
        dataPeriodDate: moment(formState.dataPeriodDate).format("YYYY/MM/DD"),
      }),
    };
    setDisableSumbitBtn(true);
    if (isEditMode) {
      InvoiceSummaryService.updateInvoiceSummary(formState.id, apiBody)
        .then(() => {
          onHide(false);
          refreshLazyData();
        })
        .finally(() => setDisableSumbitBtn(false));
    } else {
      InvoiceSummaryService.supplyInvoiceSummary(apiBody, invoiceSummaryData.id)
        .then(() => {
          onHide(false);
          refreshLazyData();
        })
        .finally(() => setDisableSumbitBtn(false));
    }
  };

  const footer = (
    <div className='flex justify-content-center'>
      <Button
        label='Đóng'
        onClick={() => {
          onHide(false);
        }}
        className='p-button-outlined p-button-secondary p-button-sm mr-2'
      />
      <Button
        label='Lưu'
        className={classNames("p-button-outlined p-button-sm mr-2", {
          hidden: hideSubmitBtn,
        })}
        disabled={disableSubmitBtn}
        onClick={() => {
          handleSubmit();
        }}
      />
    </div>
  );

  useEffect(() => {
    if (invoiceSummaryData && open) {
      dispatch({
        type: "all",
        payload: {
          id: invoiceSummaryData.id,
          dataPeriod: invoiceSummaryData.dataPeriod,
          dataPeriodDate: invoiceSummaryData.dataPeriodDate
            ? new Date(invoiceSummaryData.dataPeriodDate)
            : "",
          monthOrQuarter: invoiceSummaryData.monthOrQuarter,
          totalSubmit: invoiceSummaryData.supplementTimes,
          year: invoiceSummaryData.year ?? null,
          isFirstSubmission: false,
          goodsType: invoiceSummaryData.goodsType,
          currencyCode: invoiceSummaryData.currencyCode,
          summaryDataNumber: invoiceSummaryData.summaryDataNumber,
          type: "other",
          multiTax: false,
          creationDate: invoiceSummaryData.creationDate
            ? new Date(invoiceSummaryData.creationDate)
            : "",
        },
      });
    }
  }, [invoiceSummaryData, open]);

  useEffect(() => {
    if (formState.isFirstSubmission) {
      dispatch({
        type: OInvoiceSummaryField.TOTAL_SUBMIT,
        payload: 0,
      });
    }
  }, [formState.isFirstSubmission]);

  useEffect(() => {
    InvoiceSummaryService.getValidSerials().then((data) => {
      setAvailableSerials(
        data.map((item) => {
          return { label: item.invoiceSerial, value: item.invoiceSerial };
        })
      );
    });
  }, []);

  useEffect(() => {
    setHideSubmitBtn(disableSubmit);
  }, [disableSubmit]);

  return (
    <Dialog
      className='bg-theme form-xs p-dialog'
      header={`${
        isEditMode ? "Cập nhật" : "Bổ sung"
      } bảng tổng hợp dữ liệu hóa đơn`}
      footer={footer}
      visible={open}
      maximized={true}
      onHide={() => {
        onHide(false);
      }}
    >
      <div className='grid mt-1'>
        <div className='col-12 flex justify-content-center align-items-center'>
          <Fieldset
            style={{ width: "100%" }}
            legend={`${
              isEditMode ? "Cập nhật" : "Bổ sung"
            } hóa đơn lấy lên bảng tổng hợp`}
            className='mt-1 pl-3 pr-3 pt-2 col-12 md:col-6 lg:col-4 p-fieldset'
          >
            <form className='flex justify-content-between align-items-start flex-wrap'>
              <div className='grid gap-2 w-full md:w-6 xl:w-4 md:pr-2'>
                <div
                  className={classNames("field align-items-center grid", {
                    "col-7": formState.dataPeriod !== ODataPeriod.DAY,
                    "col-12 flex-auto":
                      formState.dataPeriod === ODataPeriod.DAY,
                  })}
                >
                  <label
                    className='col-4'
                    htmlFor='name'
                    style={{ width: "6.75rem" }}
                  >
                    Kỳ dữ liệu
                    <span className='required'>*</span>
                  </label>
                  <div
                    className={classNames(
                      "col-8 grid align-items-center pb-0 pt-2 h-full",
                      {
                        "flex-auto": formState.dataPeriod === ODataPeriod.DAY,
                      }
                    )}
                  >
                    <Dropdown
                      disabled
                      className=' col-6 pt-0 pb-0'
                      id='name'
                      value={formState.dataPeriod}
                      options={[
                        {
                          label: "Ngày",
                          value: ODataPeriod.DAY,
                        },
                        {
                          label: "Tháng",
                          value: ODataPeriod.MONTH,
                        },
                        {
                          label: "Quý",
                          value: ODataPeriod.QUARTER,
                        },
                      ]}
                      onChange={(e) => {
                        if (e.value === ODataPeriod.DAY) {
                          dispatch({
                            type: OInvoiceSummaryField.YEAR,
                            payload: "",
                          });
                        }
                        dispatch({
                          type: OInvoiceSummaryField.DATA_PERIOD,
                          payload: e.value,
                        });
                      }}
                    />
                    <div className='col-6 p-0 pl-1 h-full flex align-items-center'>
                      {formState.dataPeriod === ODataPeriod.DAY && (
                        <Calendar
                          disabled
                          className='w-full'
                          id='typeInvoiceId'
                          value={formState.dataPeriodDate}
                          dateFormat={"dd/mm/yy"}
                          onChange={(e) =>
                            dispatch({
                              type: OInvoiceSummaryField.DATA_PERIOD_DATE,
                              payload: e.value,
                            })
                          }
                        />
                      )}
                      {formState.dataPeriod !== ODataPeriod.DAY && (
                        <Dropdown
                          disabled
                          className='w-full '
                          options={getMQDropdownValues(formState.dataPeriod)}
                          placeholder={"Tháng hoặc quý"}
                          value={formState.monthOrQuarter}
                          onChange={(e) => {
                            dispatch({
                              type: OInvoiceSummaryField.MONTH_OR_QUARTER,
                              payload: e.target.value,
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className={classNames(
                    "field align-items-center grid flex-auto",
                    {
                      "col-5": formState.dataPeriod !== ODataPeriod.DAY,
                      hidden: formState.dataPeriod === ODataPeriod.DAY,
                    }
                  )}
                >
                  <label className='col-3' htmlFor='typeInvoiceId'>
                    Năm
                    <span className='required'>*</span>
                  </label>
                  <div className='col-9'>
                    <InputText
                      className=' w-full'
                      id='typeInvoiceId'
                      disabled
                      value={formState.year}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.YEAR,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className='field flex col-4 align-items-center'>
                  <label
                    className='mt-0 mb-0'
                    htmlFor='typeInvoiceId'
                    style={{ minWidth: "6.25rem" }}
                  >
                    Lần đầu
                  </label>
                  <Checkbox
                    checked={formState.isFirstSubmission}
                    disabled
                    onChange={(e) =>
                      dispatch({
                        type: OInvoiceSummaryField.IS_FIRST_SUBMISSION,
                        payload: e.checked,
                      })
                    }
                  />
                </div>
                <div className='field grid col-8 align-items-center flex-auto'>
                  <label className='mt-0 col-4 mb-0' htmlFor='typeInvoiceId'>
                    Bổ sung lần thứ
                    <span
                      className={classNames("required", { hidden: isEditMode })}
                    >
                      *
                    </span>
                  </label>
                  <div className='col-8 '>
                    <InputText
                      disabled={formState.isFirstSubmission}
                      className='w-full'
                      value={formState.totalSubmit}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.TOTAL_SUBMIT,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className='grid gap-2 w-full md:w-6 xl:w-4 xl:pr-2'>
                <div className='field align-items-center grid col-6'>
                  <label
                    className='col-5'
                    htmlFor='typeInvoiceId'
                    style={{ width: "5rem" }}
                  >
                    Số
                  </label>
                  <div className='col-7'>
                    <InputText
                      disabled={formState.isFirstSubmission || !isEditMode}
                      className=' w-full'
                      id='typeInvoiceId'
                      value={formState.summaryDataNumber}
                      onChange={(e) =>
                        dispatch({
                          type: OInvoiceSummaryField.SUMMARY_DATA_NUMBER,
                          payload: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className='field align-items-center grid col-6 flex-auto'>
                  <label className='col-4' htmlFor='typeInvoiceId'>
                    Ngày lập
                    <span className='required'>*</span>
                  </label>
                  <div className='col-8 pl-0'>
                    <Calendar
                      className=' w-full'
                      dateFormat='dd/mm/yy'
                      value={formState.creationDate}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.CREATION_DATE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>
                <div className='field grid align-items-center col-12 flex-auto'>
                  <label
                    className=''
                    style={{ minWidth: "5.25rem", paddingLeft: 3 }}
                  >
                    Ký hiệu
                    <span className='required'>*</span>
                  </label>
                  <Dropdown
                    name='invoiceSerial'
                    style={{ flex: "1 auto" }}
                    value={selectedSerial}
                    onChange={(e) => setSelectedSerial(e.value)}
                    options={availableSerials}
                    placeholder='Chọn ký hiệu hóa đơn'
                    filter
                  />
                </div>
              </div>
              <div className='grid gap-2 w-full md:w-6 xl:w-4'>
                <div className='field grid col-7 align-items-center'>
                  <label className='col-5' htmlFor='name'>
                    Loại hàng hóa
                    <span className='required'>*</span>
                  </label>
                  <div className='col-7 pl-0'>
                    <Dropdown
                      className='w-full'
                      id='name'
                      value={formState.goodsType}
                      options={[
                        {
                          label: "Khác",
                          value: OInvoiceGoodsType.OTHERS,
                        },
                        {
                          label: "Vận tải hàng không",
                          value: OInvoiceGoodsType.FLIGHT_GOODS,
                        },
                      ]}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.GOODS_TYPE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>

                <div className='field grid col-5 algin-items-center flex-auto'>
                  <label className='col-5' htmlFor='name'>
                    Mã tiền tệ
                    <span className='required'>*</span>
                  </label>
                  <div className='col-7 flex align-items-center'>
                    <Dropdown
                      filter
                      className=' w-full'
                      id='name'
                      disabled={!isEditMode || summaryDataDetails.length > 0}
                      value={formState.currencyCode}
                      options={currencies.map((currency) => {
                        return {
                          label: currency.code,
                          value: currency.code,
                        };
                      })}
                      onChange={(e) => {
                        dispatch({
                          type: OInvoiceSummaryField.CURRENCY_CODE,
                          payload: e.value,
                        });
                      }}
                    />
                  </div>
                </div>
                <div
                  className='field flex align-items-center gap-2 col-12 pl-0'
                  style={{ height: 27 }}
                >
                  <label
                    className='mt-0 mb-0'
                    htmlFor='typeInvoiceId'
                    style={{ minWidth: "7rem" }}
                  >
                    Sử dụng nhiều loại thuế trong một hóa đơn
                  </label>
                  <Checkbox
                    checked={formState.multiTax}
                    onChange={(e) =>
                      dispatch({
                        type: "multiTax",
                        payload: e.checked,
                      })
                    }
                  />
                </div>
              </div>
            </form>
          </Fieldset>
        </div>
        <div className='col-12'>
          <SummaryDetailsTable
            isEditMode={isEditMode}
            formState={formState}
            setSummaryDetails={setSummaryDataDetails}
            id={invoiceSummaryData?.id}
            summaryDetails={summaryDataDetails}
            serial={selectedSerial}
          />
        </div>
      </div>
    </Dialog>
  );
};
